import { PanelRightIcon } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import {
  Group,
  Panel,
  Separator,
  useDefaultLayout
} from 'react-resizable-panels';
import { useLocalStorage } from 'usehooks-ts';

import RightSidebar from './index';

import { useMainSidebar } from '@/hooks/useMainSidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';

const NARROW_VIEWPORT_BREAKPOINT = 960;

interface Props {
  children: ReactNode;
}

const RIGHT_SIDEBAR_DEFAULT_SIZE = 414;
const RIGHT_SIDEBAR_MIN_SIZE = 320;
const RIGHT_SIDEBAR_LAYOUT_ID = 'investmentsDirectRightSidebarLayout';
const MAIN_CONTENT_PANEL_ID = 'investmentsDirectMainContentPanel';
const RIGHT_SIDEBAR_PANEL_ID = 'investmentsDirectRightSidebarPanel';

export const RightSidebarWrapper = ({ children }: Props) => {
  const [isOpen, setIsOpen] = useLocalStorage('isOpen', true);
  const { setIsOpen: setMainSidebarOpen } = useMainSidebar();
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < NARROW_VIEWPORT_BREAKPOINT
  );
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: RIGHT_SIDEBAR_LAYOUT_ID,
    panelIds: [MAIN_CONTENT_PANEL_ID, RIGHT_SIDEBAR_PANEL_ID]
  });

  useEffect(() => {
    const mql = window.matchMedia(
      `(max-width: ${NARROW_VIEWPORT_BREAKPOINT - 1}px)`
    );
    const onChange = () => {
      setIsNarrowViewport(window.innerWidth < NARROW_VIEWPORT_BREAKPOINT);
    };
    mql.addEventListener('change', onChange);
    onChange();
    return () => mql.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (isNarrowViewport && isOpen) {
      setMainSidebarOpen(false);
    }
  }, [isNarrowViewport, isOpen, setMainSidebarOpen]);

  const handlePanelRightButtonClick = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  if (isOpen) {
    return (
      <Group
        className="min-w-0 max-h-[calc(100dvh-64px-49px-66px)] md:max-h-[calc(100dvh-64px-49px)]"
        defaultLayout={defaultLayout}
        onLayoutChanged={onLayoutChanged}
      >
        <Panel
          className="overflow-y-auto"
          id={MAIN_CONTENT_PANEL_ID}
        >
          {children}
        </Panel>
        <Separator className="group relative w-1.5 bg-transparent transition-colors hover:bg-blue-500/10 active:bg-blue-500/20">
          <div className="bg-border absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors group-hover:bg-blue-500 group-active:bg-blue-600" />
        </Separator>
        <Panel
          className="min-w-0 overflow-x-hidden overflow-y-auto"
          defaultSize={isNarrowViewport ? 320 : RIGHT_SIDEBAR_DEFAULT_SIZE}
          id={RIGHT_SIDEBAR_PANEL_ID}
          maxSize="50%"
          minSize={isNarrowViewport ? 260 : RIGHT_SIDEBAR_MIN_SIZE}
        >
          <RightSidebar setIsOpen={setIsOpen} />
        </Panel>
      </Group>
    );
  }

  return (
    <div className="grid grid-cols-[1fr_70px]">
      {children}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className="bg-card sticky top-17.5 grid size-12.5 place-content-center rounded-2xl border-l shadow-lg"
            onClick={handlePanelRightButtonClick}
          >
            <PanelRightIcon />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>
            Open Right
            <br />
            Navigation
          </p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};
